import type { ModifierArguments, Placement } from "@popperjs/core";
import { createPopper } from "@popperjs/core";
import { styled } from "@linaria/react";
import React from "react";
import ReactDOM from "react-dom";
import { color, zIndex as zIndexEnum } from "..";
import useControllableState from "../lib/hooks/useControllableState";
import useEnsuredForwardedRef from "../lib/hooks/useEnsuredForwardedRef";
import useOutsideClick from "../lib/hooks/useOutsideClick";
import { MODAL_ID } from "../Modal";
import Toggle from "../Toggle";

export const EDGE_MARGIN_PX = 8;

const setFullWidth = (data: ModifierArguments<Record<string, unknown>>) => {
  const state = data.state;
  const firstPart = state.placement.split("-")[0];

  if (firstPart && !["top", "bottom"].includes(firstPart)) return state;
  const { width, x } = state.rects.reference;
  const popperStyle = state.styles["popper"];
  if (!popperStyle) return state;
  popperStyle.width = `${width}px`;
  popperStyle.left = `${x}px`;
  return state;
};

export interface MousePosition {
  x: number;
  y: number;
}

const setMousePlacement = (
  data: ModifierArguments<Record<string, unknown>>,
  mouseXY: MousePosition,
  offsetXY: MousePosition,
) => {
  const state = data.state;
  const popperXposition = mouseXY.x + offsetXY.x;
  const popperYposition = mouseXY.y + offsetXY.y;
  const popperStyle = state.styles["popper"];
  if (!popperStyle) return state;
  popperStyle.left = `${popperXposition}px`;
  popperStyle.top = `${popperYposition}px`;
  return state;
};

export interface PopperProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactElement; // anchor
  overlay: React.ReactNode | (() => React.ReactNode); // Popping content
  keepInDom?: boolean; // default false
  hover?: boolean; // listen to hover events on child to control overlay
  click?: boolean; // listen to click events on child to control overlay (also listen to focus for kb accessibility)
  focus?: boolean; // listen to focus events on child to control overlay
  // In controlled mode, trigger open/close manually with ref handles or opened prop (see Tooltip Storybook example)

  /**
   * Group id of multiple tooltips
   * When a tooltip with a group id is opened, the group become active for `groupActiveDuration` ms
   * During this duration, if you open another tooltip of the same group, the open delay will be `openGroupDelay`
   * It allows to reduce the delay of opening when close tooltip was opened before
   */
  groupId?: string; // group id of tooltips, when a tooltip of a group is opened, the group be
  openDelay?: number; // opening delay on hover/focus, default 0
  openGroupDelay?: number; // opening delay when group is active, default 0
  closeDelay?: number; // closing delay on hover/focus, default 0
  groupActiveDuration?: number; // delay before group is considered inactive, default 4000

  keepOpenOnOverlayActive?: boolean; // default false
  overlayInactiveCloseDelay?: number; // works with keepOpenOnOverlayActive=true closing delay on hover/focus stop on overlay, default 0
  closeOnOutsideClick?: boolean; // default true
  outsideClickCallback?: () => void; // callback on outside click
  gap?: number; // add space between overlay (or arrow if present) and wrapper
  placement?: Placement; // https://popper.js.org/popper-documentation.html#Popper.placements, default auto
  defaultOpened?: boolean; // initial opened value, use when uncontrolled
  opened?: boolean; // opened value, use for controlled variant
  setOpened?: React.Dispatch<React.SetStateAction<boolean>>; // callback for toggle request, use when controlled
  matchChildWidth?: boolean; // make Popper the same width as child. Use with placements top/bottom
  animationTimeout?: number; // delay before `.inactive` class is set (ms), auto-set if fadeOut, can be forced to fit custom wrapper animation timeout
  backgroundColor?: string; // sets overlay background color arrow extrusion color, default white
  borderColor?: string; // sets overlay border color and arrow's border color, default transparent
  borderWidth?: number; // sets overlay border width in px and arrow's border width, default 0
  arrowWidth?: number; // sets arrow width, default 0 (no arrow)
  strategy?: "fixed" | "absolute"; // defaults to fixed
  mouseXY?: MousePosition; // mouse x position to place the overlay
  offsetXY?: MousePosition; // offset x, y position relative to mouseX position
}

interface OverlayProps {
  color?: string;
  backgroundColor: string;
  borderColor: string;
  borderWidth: number;
  arrowWidth: number;
  arrowed: boolean;
  zIndex: number;
}

const Overlay = styled.div<OverlayProps>`
  will-change: top, left, opacity, transform !important;
  color: ${p => (p.color ? p.color : "inherit")};
  background-color: ${p => p.backgroundColor};
  border: ${p => p.borderWidth}px solid ${p => p.borderColor};
  z-index: ${p => p.zIndex};
  // used for full viewport chart (body -> none when active)
  pointer-events: auto;
  [data-popper-arrow] {
    position: absolute;
    border: ${p => p.arrowWidth}px solid transparent;
    ::after {
      position: absolute;
      content: "";
      border: ${p => p.arrowWidth}px solid transparent;
    }
  }
  &[data-popper-placement^="top"] {
    transform-origin: bottom center;
    [data-popper-arrow] {
      border-bottom-width: 0;
      border-top-color: inherit;
      bottom: ${p => -p.arrowWidth}px;
      ::after {
        bottom: calc(${p => -p.arrowWidth}px + ${p => p.borderWidth}px);
        left: calc(50% + ${p => -p.arrowWidth}px);
        border-top-color: ${p => p.backgroundColor};
      }
    }
  }
  &[data-popper-placement^="bottom"] {
    transform-origin: top center;
    [data-popper-arrow] {
      border-top-width: 0;
      border-bottom-color: inherit;
      top: ${p => -p.arrowWidth}px;
      ::after {
        top: calc(${p => -p.arrowWidth}px + ${p => p.borderWidth}px);
        left: calc(50% + ${p => -p.arrowWidth}px);
        border-bottom-color: ${p => p.backgroundColor};
      }
    }
  }
  &[data-popper-placement^="right"] {
    transform-origin: center left;
    [data-popper-arrow] {
      border-left-width: 0;
      border-right-color: inherit;
      /* top: calc(50% + ${p => -p.arrowWidth}px) ; */
      left: ${p => -p.arrowWidth}px;
      ::after {
        left: calc(${p => -p.arrowWidth}px + ${p => p.borderWidth}px);
        top: calc(50% + ${p => -p.arrowWidth}px) !important;
        border-right-color: ${p => p.backgroundColor};
      }
    }
  }
  &[data-popper-placement^="left"] {
    transform-origin: center right;
    [data-popper-arrow] {
      border-right-width: 0;
      border-left-color: inherit;
      right: ${p => -p.arrowWidth}px;
      ::after {
        right: calc(${p => -p.arrowWidth}px + ${p => p.borderWidth}px);
        top: calc(50% + ${p => -p.arrowWidth}px);
        border-left-color: ${p => p.backgroundColor};
      }
    }
  }
`;

const popperGroupLastClosedTime: { [key in string]: null | number } = {};

const Popper = (
  {
    children,
    overlay: _overlay,
    keepInDom = false,
    gap = 0,
    closeOnOutsideClick = true,
    outsideClickCallback,
    backgroundColor = color.hydrogen,
    borderColor = "transparent",
    borderWidth = 0,
    arrowWidth = 0,
    groupId,
    openDelay = 0,
    openGroupDelay = 0,
    closeDelay = 0,
    groupActiveDuration = 4000,
    placement = "auto",
    defaultOpened = false,
    opened: openedProp,
    setOpened: setOpenedProp,
    matchChildWidth = false,
    animationTimeout = 0,
    click = false,
    hover = false,
    focus = false,
    keepOpenOnOverlayActive = false,
    strategy = "fixed",
    mouseXY,
    offsetXY,
    ...htmlProps
  }: PopperProps,
  forwardedRef: React.Ref<HTMLElement>,
) => {
  const [zIndex, setZIndex] = React.useState(zIndexEnum.popper);
  const arrowed = Boolean(arrowWidth);
  const wrapperElement = useEnsuredForwardedRef(forwardedRef);
  const overlayElement = React.useRef<null | HTMLDivElement>(null);
  const [opened, setOpened] = useControllableState(openedProp, setOpenedProp, defaultOpened);

  const overlayIsLazy = typeof _overlay === "function";
  const visible = overlayIsLazy ? opened : Boolean(_overlay) && opened;
  const openedSetTimeout = React.useRef<number>();
  const requestSetOpened = React.useCallback(
    (nextOpened: boolean, delay?: number) => {
      if (openedSetTimeout.current !== undefined) window.clearTimeout(openedSetTimeout.current);
      const lastClosedTime = groupId && popperGroupLastClosedTime[groupId];
      const delayFromLastClosed = lastClosedTime && Date.now() - lastClosedTime;
      const computedDelay =
        Number(delay) &&
        nextOpened &&
        groupId &&
        delayFromLastClosed &&
        delayFromLastClosed < groupActiveDuration
          ? openGroupDelay
          : delay;
      if (groupId && opened && !nextOpened) {
        popperGroupLastClosedTime[groupId] = Date.now();
      }
      openedSetTimeout.current = window.setTimeout(() => {
        setOpened(nextOpened);
      }, computedDelay);
    },
    [opened, setOpened, groupId, groupActiveDuration, openGroupDelay],
  );

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      requestSetOpened(!opened);
      children.props.onClick?.(e);
    },
    [children.props, opened, requestSetOpened],
  );
  const handleFocus = React.useCallback(
    (e: React.FocusEvent<HTMLElement>) => {
      requestSetOpened(true, openDelay);
      children.props.onFocus?.(e);
    },
    [children.props, openDelay, requestSetOpened],
  );
  const handleBlur = React.useCallback(
    (e: React.FocusEvent<HTMLElement>) => {
      requestSetOpened(false, closeDelay);
      children.props.onBlur?.(e);
    },
    [children.props, closeDelay, requestSetOpened],
  );
  const handleMouseEnter = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      requestSetOpened(true, openDelay);
      children.props.onMouseEnter?.(e);
    },
    [children.props, openDelay, requestSetOpened],
  );
  const handleMouseLeave = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      requestSetOpened(false, closeDelay);
      children.props.onMouseLeave?.(e);
    },
    [children.props, closeDelay, requestSetOpened],
  );
  const handleOverlayActive = React.useCallback(() => {
    if (!visible) return;
    if (openedSetTimeout.current !== undefined) window.clearTimeout(openedSetTimeout.current);
    setOpened(true);
  }, [visible, setOpened]);
  const handleOverlayInactive = React.useCallback(() => {
    requestSetOpened(false, closeDelay);
  }, [closeDelay, requestSetOpened]);

  const calcVerticalArrow = placement.includes("start")
    ? (-arrowWidth + borderWidth) * 2
    : placement.includes("end")
      ? -(-arrowWidth + borderWidth) * 2
      : 0;
  const distanceVerticalOffset = arrowed ? calcVerticalArrow : 0;
  const distanceHorizontalOffset = gap + (arrowed ? arrowWidth : 0);

  useOutsideClick(wrapperElement, [overlayElement, wrapperElement], () => {
    if (closeOnOutsideClick) {
      outsideClickCallback?.();
      requestSetOpened(false);
    }
  });

  React.useLayoutEffect(() => {
    const wrapper = wrapperElement.current;
    const overlay = overlayElement.current;
    if (!wrapper || !overlay) return;
    const ref = createPopper(wrapper, overlay, {
      strategy,
      placement,
      modifiers: [
        {
          name: "offset",
          enabled: true,
          options: { offset: [distanceVerticalOffset, distanceHorizontalOffset] },
        },
        { name: "arrow", enabled: arrowed }, //, options: { padding: arrowWidth * 2 }
        {
          name: "computeStyles",
          enabled: true,
          options: {
            adaptive: false, // not compatible with animations
            gpuAcceleration: false, // not compatible with transform animations (force use of top/left for positioning)
          },
        },
        {
          name: "preventOverflow",
          options: {
            padding: EDGE_MARGIN_PX, // keep the poppers 8px away from the edges
          },
        },
        {
          name: "setFullWidth",
          enabled: matchChildWidth,
          phase: "beforeWrite",
          fn: setFullWidth,
        },
        {
          name: "overlayOnMouse",
          enabled: mouseXY !== undefined,
          phase: "beforeWrite",
          fn: data =>
            setMousePlacement(data, mouseXY ?? { x: 0, y: 0 }, offsetXY ?? { x: 0, y: 0 }),
        },
      ],
    });
    return () => ref?.destroy();
  }, [
    matchChildWidth,
    placement,
    wrapperElement,
    visible,
    distanceHorizontalOffset,
    distanceVerticalOffset,
    arrowWidth,
    arrowed,
    strategy,
    offsetXY,
    mouseXY,
  ]);

  React.useEffect(
    () => () => {
      if (openedSetTimeout.current !== undefined) window.clearTimeout(openedSetTimeout.current);
    },
    [],
  );

  React.useLayoutEffect(() => {
    if (!wrapperElement.current) throw new Error("Missing wrapperElement in Popper");
    const modalElement = window.document.getElementById(MODAL_ID);
    if (modalElement?.contains(wrapperElement.current)) setZIndex(zIndexEnum.modalPopper);
  }, [wrapperElement]);

  return (
    <>
      {React.cloneElement(children, {
        ...(click ? { onClick: handleClick } : null),
        ...(hover ? { onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave } : null),
        ...(focus ? { onFocus: handleFocus, onBlur: handleBlur } : null),
        ref: (node: HTMLElement | null) => {
          (wrapperElement as React.MutableRefObject<HTMLElement | null>).current = node;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const ref = (children as any).ref as
            | React.MutableRefObject<HTMLElement | null>
            | undefined;
          if (ref) {
            ref.current = node;
          }
        },
      })}
      {ReactDOM.createPortal(
        <Toggle timeout={animationTimeout} active={visible}>
          {keepInDom || visible ? (
            <Overlay
              zIndex={zIndex}
              backgroundColor={backgroundColor}
              borderColor={borderColor}
              borderWidth={borderWidth}
              arrowWidth={arrowWidth}
              arrowed={arrowed}
              onMouseEnter={keepOpenOnOverlayActive ? handleOverlayActive : undefined}
              onMouseLeave={keepOpenOnOverlayActive ? handleOverlayInactive : undefined}
              onFocus={keepOpenOnOverlayActive ? handleOverlayActive : undefined}
              onBlur={keepOpenOnOverlayActive ? handleOverlayInactive : undefined}
              ref={overlayElement}
              {...htmlProps}
            >
              {typeof _overlay === "function" ? _overlay() : _overlay}
              {arrowed ? <div data-popper-arrow="" /> : null}
            </Overlay>
          ) : null}
        </Toggle>,
        document.body,
      )}
    </>
  );
};

export default React.memo(React.forwardRef(Popper));
